/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LogProfileFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Log Profiles module.
----------------------------------------------------------------*/


import { useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, Copy, FileText } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { navigateToLogProfileDuplicate } from './logProfilesActions';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { LogProfile as LogProfileType } from './types';
import {
    useLogProfileForm, useLogProfileData, useQuickList,
    useLogProfilePermissions, useLogProfileSubmit, usePermissionsUI,
} from './hooks';
import { LogProfileBasicInfoBox } from './components/LogProfileBasicInfoBox';
import { LogProfilePermissionsBox } from './components/LogProfilePermissionsBox';
import { LogProfileQuickListModal } from './components/LogProfileQuickListModal';
import { type PermKey } from './components/LogPermissionButtonGroup';

const LIST_PATH = '/log-profiles';

export default function LogProfileFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { setCurrentPageTitle } = useNavigationStore();
    const { showInfo } = useNotifications();

    const duplicateId = searchParams.get('duplicate');
    const resetParam = searchParams.get('_reset');
    const mode = id === 'new' || duplicateId ? 'create' : 'edit';

    useFixedActionBar(true);

    // -- Hooks -------------------------------------------------
    const { formData, originalData, setFormData, setOriginalData, handleInputChange, hasChanges, errors, setErrors } = useLogProfileForm();
    const { menuItems, loading } = useLogProfileData(mode, id, duplicateId, setFormData, setOriginalData, resetParam);
    const { getPermissionValue, allChildrenHavePermission, isPermissionInherited, handlePermissionChange } = useLogProfilePermissions(formData, menuItems);
    const { expandedSections, expandedGroups, searchQuery, setSearchQuery, toggleSection, toggleGroup, handleToggleAll, areAllExpanded, organizeMenuItems } = usePermissionsUI(menuItems);
    const { showQuickList, setShowQuickList, quickListSearch, setQuickListSearch, filteredQuickList, isQuickListLoading } = useQuickList();
    const { saving, isDeleting, showDeleteModal, errors: submitErrors, setErrors: setSubmitErrors, handleSubmit, handleDelete, handleDeleteClick, handleCancel: handleDeleteCancel } = useLogProfileSubmit(mode, id, formData, hasChanges, setOriginalData);

    // -- Save --------------------------------------------------
    const handleSaveInternal = useCallback(async (): Promise<LogProfileType | undefined> => handleSubmit(), [handleSubmit]);
    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: LIST_PATH, newPath: `${LIST_PATH}/new`,
        getEditPath: (saved) => `${LIST_PATH}/${saved.id}`,
        mode, storageKey: 'log-profiles-save-action',
    });

    // -- Cancel ------------------------------------------------
    const handleCancel = useCallback(() => {
        if (mode === 'create') {
            const leafSlugs: string[] = [];
            menuItems.forEach((item) => {
                if (item.level === 3 && item.slug) leafSlugs.push(item.slug);
                else if (item.level === 2 && item.slug) {
                    if (!menuItems.some((c) => c.parentId === item.id && c.level === 3)) leafSlugs.push(item.slug);
                }
            });
            const empty = leafSlugs.map((slug) => ({ moduleSlug: slug, logCreate: false, logEdit: false, logDelete: false, notifyCreate: false, notifyEdit: false, notifyDelete: false }));
            setFormData({ name: '', description: '', permissions: empty });
            setOriginalData({ name: '', description: '', permissions: empty.map((p) => ({ ...p })) });
        } else {
            setFormData({ name: originalData.name, description: originalData.description, permissions: originalData.permissions.map((p) => ({ ...p })) });
        }
        setErrors({});
        setSubmitErrors({});
    }, [mode, menuItems, setFormData, setOriginalData, originalData, setErrors, setSubmitErrors]);

    const allErrors = { ...errors, ...submitErrors };

    return (
        <div className="p-6">
            <ModuleFormHeader
                icon={<FileText className="h-5 w-5 text-primary" />}
                title={mode === 'create' ? t('logProfiles.forms.createTitle') : `${t('logProfiles.forms.editTitle')} - ${formData.name || ''}`}
                backButtonText={t('common.list')}
                onBack={() => navigate(LIST_PATH)}
                actions={[
                    { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('logProfiles.quickList'), onClick: () => setShowQuickList(true) },
                    ...(mode === 'edit' && id && id !== 'new' && !duplicateId
                        ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('common.duplicate'), onClick: () => navigateToLogProfileDuplicate(navigate, id!) }]
                        : []),
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('logProfiles.addLogProfile'), onClick: () => navigate(`${LIST_PATH}/new`) },
                ]}
            />

            <div className="mt-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                    <LogProfileBasicInfoBox
                        name={formData.name} description={formData.description} errors={allErrors}
                        onNameChange={(v) => handleInputChange('name', v)}
                        onDescriptionChange={(v) => handleInputChange('description', v)}
                    />
                    <LogProfilePermissionsBox
                        loading={loading} organizeMenuItems={organizeMenuItems}
                        expandedSections={expandedSections} expandedGroups={expandedGroups}
                        searchQuery={searchQuery} areAllExpanded={areAllExpanded}
                        onSearchChange={setSearchQuery} onToggleAll={handleToggleAll}
                        onToggleSection={toggleSection} onToggleGroup={toggleGroup}
                        getPermissionValue={getPermissionValue}
                        allChildrenHavePermission={allChildrenHavePermission}
                        isPermissionInherited={isPermissionInherited}
                        onPermissionChange={(id, key, val, sf) => handlePermissionChange(id, key as PermKey, val, sf)}
                        setFormData={setFormData}
                    />
                </form>
            </div>

            <LogProfileQuickListModal
                isOpen={showQuickList} quickListSearch={quickListSearch}
                filteredQuickList={filteredQuickList} isQuickListLoading={isQuickListLoading}
                currentItemId={mode === 'edit' && !duplicateId ? id : null}
                onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
                onSearchChange={setQuickListSearch}
                onSelect={(profileId) => navigate(`${LIST_PATH}/${profileId}`)}
            />

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction} onCancel={handleCancel} onDelete={handleDeleteClick}
                saveText={saving ? t('common.loading') : t('common.save')}
                cancelText={t('common.cancel')} deleteText={t('common.delete')}
                saveDisabled={saving || isDeleting} deleteDisabled={saving || isDeleting}
                isLoading={saving || isDeleting} isEditMode={mode === 'edit'}
                hasChanges={hasChanges} useSaveDropdown showSave saveDropdownStorageKey="log-profiles-save-action"
            />

            <ConfirmationModal
                isOpen={showDeleteModal} onClose={handleDeleteCancel} onConfirm={handleDelete}
                title={t('logProfiles.delete.title')} message={t('logProfiles.confirmDelete.generic')}
                confirmText={t('common.delete')} cancelText={t('common.cancel')} type="danger" isLoading={isDeleting}
            />
        </div>
    );
}
